import Fuse from 'fuse.js';

import { type SearchIndexItem, type SearchFilterType } from '../../domain/types/search.types';

/**
 * MOTOR DE BÚSQUEDA LOCAL (Agnóstico a la UI)
 * 
 * Utiliza Fuse.js para búsquedas difusas en memoria y aplica un algoritmo
 * de ranking dinámico basado en filtros (Recomendado, Cercanía, Reputación).
 * Reemplazado FlexSearch por Fuse.js debido a incompatibilidades de import.meta en Metro Web.
 */
export class LocalSearchEngine {
  private index: Fuse<SearchIndexItem> | null = null;
  private itemsList: SearchIndexItem[] = [];



  /**
   * Indexa una lista de médicos/productos.
   */
  public async indexItems(items: SearchIndexItem[]): Promise<void> {
    this.itemsList = items;
    
    // Inicializar Fuse con la lista de items
    this.index = new Fuse(this.itemsList, {
      keys: [
        { name: "nombre", weight: 2 },
        { name: "titulo_principal", weight: 1.8 },
        { name: "especialidad", weight: 1.5 },
        { name: "sub_especialidad", weight: 1 },
        { name: "search_blob", weight: 0.8 }
      ],
      includeScore: true,
      threshold: 0.3, // Reducido a 0.3 para evitar que coincidencias lejanas ("fredo" -> "evelyn") den falsos positivos
      ignoreLocation: true,
      minMatchCharLength: 1, 
    });

    console.info(`[LocalSearchEngine] Indexados ${items.length} elementos con Fuse.js.`);
  }

  /**
   * Añade un único elemento al índice (Indexación Incremental)
   */
  public async addItem(item: SearchIndexItem): Promise<void> {
    this.itemsList.push(item);
    if (this.index) {
      this.index.add(item);
    }
  }

  /**
   * Realiza una búsqueda y aplica el algoritmo de pesos basado en el filtro.
   * @param query Texto a buscar
   * @param userLat Latitud actual del usuario
   * @param userLng Longitud actual del usuario
   * @param filter Filtro de ordenamiento
   * @param targetCityId ID de ciudad opcional para filtrar estrictamente
   * @param targetModalities Lista de modalidades seleccionadas para filtrar (OR)
   */
  public async search(
    query: string, 
    userLat: number, 
    userLng: number, 
    filter: SearchFilterType = 'recomendado',
    targetCityId: number | null = null,
    targetModalities: string[] = []
  ): Promise<SearchIndexItem[]> {
    if (!this.index && this.itemsList.length === 0) return [];

    let results: SearchIndexItem[] = [];

    // Si el query está vacío, evaluamos todos los elementos (solo para ordenar por geolocalización)
    if (!query || query.trim() === '') {
      results = this.itemsList;
    } else {
      const fuseResults = this.index!.search(query, { limit: 20 });
      results = fuseResults.map(r => r.item);
    }

    // Filtrado estricto por ciudad (Turismo Médico)
    if (targetCityId !== null) {
      results = results.filter(item => item.ciudad_id === targetCityId);
    }

    // Filtrado estricto por modalidades seleccionadas (Lógica OR: coincide si ofrece al menos una de las elegidas)
    if (targetModalities && targetModalities.length > 0) {
      // Mapeo de sinónimos para asegurar coincidencia con el backend (search_blob)
      const modalitySynonyms: Record<string, string[]> = {
        'presencial': ['presencial', 'consultorio'],
        'virtual': ['virtual', 'online', 'telemedicina'],
        'domicilio': ['domicilio', 'casa', 'hogar']
      };

      results = results.filter(item => {
        const blob = item.search_blob?.toLowerCase() || '';
        return targetModalities.some(modality => {
          const synonyms = modalitySynonyms[modality.toLowerCase()] || [modality.toLowerCase()];
          return synonyms.some(synonym => blob.includes(synonym));
        });
      });
    }

    // Aplicamos algoritmo de Ranking y Prioridad de Badge
    const rankedResults = results
      .map(item => {
        const latTarget = item.lat != null && item.lat !== 0 ? item.lat : null;
        const lngTarget = item.lng != null && item.lng !== 0 ? item.lng : null;
        
        let distanceScore = 0;
        let distanceKmFinal: number | undefined = undefined;

        if (latTarget !== null && lngTarget !== null) {
          const { score, distanceKm } = this.calculateDistanceScore(userLat, userLng, latTarget, lngTarget);
          distanceScore = score;
          
          // Solo guardamos la distancia real si el usuario también tiene GPS válido
          if (userLat !== 0 || userLng !== 0) {
            distanceKmFinal = distanceKm;
          }
        }
        
        item.distance_km = distanceKmFinal;

        const reputationScore = (item.reputacion_score ?? 0) / 100; // Normalizado 0-1

        let finalScore = 0;

        switch (filter) {
          case 'cercania':
            finalScore = distanceScore; // 100% Cercanía
            break;
          
          case 'reputacion':
            finalScore = reputationScore; // 100% Reputación
            break;
          
          case 'recomendado':
          default:
            // FÓRMULA DE MISIÓN CRÍTICA (AuraScore): (Cercanía * 0.4) + (Reputación * 0.6)
            finalScore = (distanceScore * 0.4) + (reputationScore * 0.6);
            
            // BOOST COMERCIAL: Los recomendados suben al top (Score > 1.0)
            if (item.badge_recomendado) {
              finalScore += 1;
            }
            break;
        }

        return { item, finalScore };
      })
      .sort((a, b) => b.finalScore - a.finalScore)
      .map(r => r.item);

    // Si fue una búsqueda sin query (geolocalizada pura), devolvemos solo el Top 20 más cercano
    if (!query || query.trim() === '') {
      return rankedResults.slice(0, 20);
    }

    return rankedResults;
  }

  /**
   * Calcula un score de cercanía (0 a 1) utilizando la Fórmula Plana del Coseno.
   * 1 = Está encima del usuario. 0 = Está muy lejos (fuera del cuadrante).
   * Es mucho más rápida que Haversine y perfecta para búsquedas intraurbanas (< 500km).
   */
  private calculateDistanceScore(lat1: number, lng1: number, lat2: number, lng2: number): { score: number, distanceKm: number } {
    if (lat1 === lat2 && lng1 === lng2) return { score: 1, distanceKm: 0 };
    if (lat1 === 0 && lng1 === 0) return { score: 0, distanceKm: 9999 }; // Sin GPS

    const PI_RAD = Math.PI / 180;
    
    // Proyección equirectangular (Flat Cosine Distance)
    const latMedRad = ((lat1 + lat2) / 2) * PI_RAD;
    const deltaLat = (lat1 - lat2);
    const deltaLng = (lng1 - lng2) * Math.cos(latMedRad);
    
    // Distancia en grados euclidianos (proyectados)
    const diffDeg = Math.hypot(deltaLat, deltaLng);
    
    // 1 grado es aproximadamente 111 kilómetros
    const distanceKm = diffDeg * 111.32;
    
    // Rango máximo para puntaje (ej. 55km = ~0.5 grados). Cualquier distancia mayor tiene score 0.
    const MAX_DISTANCE_KM = 55;
    if (Number.isNaN(distanceKm) || distanceKm >= MAX_DISTANCE_KM) return { score: 0, distanceKm: Number.isNaN(distanceKm) ? 9999 : distanceKm };

    const score = Math.max(0, 1 - (distanceKm / MAX_DISTANCE_KM));
    return { score, distanceKm };
  }
}
